import {
  CATEGORIES,
  type Category,
  countedWays,
  dec,
  defaultsFor,
  formatQuantity,
  HOP_FORM_LABELS,
  sum,
  toCanonical,
  toFixedTrimmed,
  UNITS,
  ZERO,
} from "@bl/domain";
import { TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { type Item, type Location, useCatalog } from "../api";
import { categoryLabel, itemChoice, showDate, withMaker } from "../format";
import { PackPicker, usablePack } from "../forms/PackPicker";
import { RegionInput } from "../forms/RegionInput";
import {
  type Candidate,
  type Decision,
  type Group,
  type GroupChoice,
  itemFor,
  lotsFor,
  type PackGuess,
  pickedOf,
} from "./stockImportState";

const DIMENSION_WORDS: Record<string, string> = {
  mass: "by weight",
  volume: "by volume",
  count: "by count",
  pitch: "by pitch",
};
/** Ollie's figure in its own unit, to four places with thousands grouped, worked in decimals. */
function quantityText(quantity: string, unit: keyof typeof UNITS): string {
  const [whole = "", fraction] = toFixedTrimmed(dec(quantity), 4).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${fraction ? `${grouped}.${fraction}` : grouped} ${UNITS[unit].label}`;
}
/** One of the report's items: what it goes in as here, and its lots. */
export function ImportGroup({
  group: g,
  choice,
  category,
  guess,
  items,
  locations,
  placeOf,
  onChange,
}: {
  group: Group;
  choice: GroupChoice;
  category: Category | null;
  guess: PackGuess;
  items: Item[];
  locations: Location[];
  placeOf: (index: number) => string;
  onChange: (choice: GroupChoice) => void;
}) {
  const [finding, setFinding] = useState(false);
  const d = choice.decision;
  const sug = g.suggestion;
  const fitting = useMemo(
    () =>
      items
        .filter((i) => !i.archived && i.dimension === g.dimension)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [items, g.dimension],
  );
  const offered = (id: string) => sug.candidates.find((c) => c.id === id);
  const mode =
    d.kind === "item" && d.itemId === sug.item?.id
      ? "suggested-item"
      : d.kind === "catalog" && d.productId === sug.catalogProduct?.id
        ? "suggested-catalog"
        : d.kind === "catalog" && offered(d.productId)
          ? `candidate:${d.productId}`
          : d.kind;
  const choose = (decision: Decision, picked?: GroupChoice["picked"]) =>
    onChange({ decision, picked, lots: lotsFor(g, decision, items, picked) });
  const chooseOffered = (c: Candidate) => choose({ kind: "catalog", productId: c.id }, pickedOf(c));
  const deciding = d.kind !== "skip" && d.kind !== "undecided" && !finding;
  const into = itemFor(g, d, items, choice.picked);
  const already = into && dec(into.onHand.quantity).gt(ZERO);
  const pack = choice.pack === undefined ? guess.pack : choice.pack;
  const usable = usablePack(pack);
  const packSize = usable ? toCanonical(usable.size, g.dimension) : null;
  const packNote = !pack
    ? "Ollie gives only weight. Left without a pack, these lots are counted in the item's pack once an invoice gives it one."
    : choice.pack !== undefined
      ? "Ollie gives only weight; the weight stays Ollie's, counted in these."
      : guess.why === "item"
        ? `The pack ${into?.displayName ?? "the item"} already comes in. Ollie gives only weight; change it if these came otherwise.`
        : guess.why === "only"
          ? "The only pack this kind of item comes in. Ollie gives only weight; change it if these came otherwise."
          : "Every lot here is a whole number of these. Ollie gives only weight; change it if these came otherwise.";
  const productName =
    d.kind === "catalog"
      ? d.productId === sug.catalogProduct?.id
        ? sug.catalogProduct.name
        : (choice.picked?.name ?? "A catalog product")
      : null;

  return (
    <div
      className={`import-group ${d.kind === "skip" ? "skipped" : d.kind === "undecided" ? "undecided" : ""}`}
    >
      <div className="import-group-head">
        <strong>{g.name}</strong>
        <span className="muted">
          {g.lots.length === 1 ? "1 lot" : `${g.lots.length} lots`}
          {g.dimension !== "mass" && g.lots.every((l) => l.unit === g.lots[0]?.unit)
            ? `, ${quantityText(sum(g.lots.map((l) => l.quantity)).toFixed(), g.lots[0]?.unit ?? "each")}`
            : ""}
        </span>
      </div>
      <div className="import-decision">
        <label className="field">
          <span>Goes in as</span>
          <select
            className="select"
            value={finding ? "find" : mode}
            onChange={(e) => {
              const v = e.target.value;
              setFinding(v === "find");
              const c = v.startsWith("candidate:") ? offered(v.slice("candidate:".length)) : null;
              if (c) chooseOffered(c);
              else if (v === "suggested-item" && sug.item)
                choose({ kind: "item", itemId: sug.item.id });
              else if (v === "suggested-catalog" && sug.catalogProduct)
                choose({ kind: "catalog", productId: sug.catalogProduct.id });
              else if (v === "new") choose({ kind: "new", name: g.name, category: g.category });
              else if (v === "item") {
                const first = fitting.find((i) => i.id !== sug.item?.id) ?? fitting[0];
                if (first) choose({ kind: "item", itemId: first.id });
              } else if (v === "skip") choose({ kind: "skip" });
            }}
          >
            {d.kind === "undecided" ? (
              <option value="undecided" disabled>
                Say what this is
              </option>
            ) : null}
            {sug.item ? (
              <option value="suggested-item">{sug.item.name}, in inventory</option>
            ) : null}
            {sug.catalogProduct ? (
              <option value="suggested-catalog">{sug.catalogProduct.name}, from the catalog</option>
            ) : null}
            {sug.candidates.map((c) => (
              <option key={c.id} value={`candidate:${c.id}`}>
                {c.name}, from the catalog{c.close ? " (check it)" : ""}
              </option>
            ))}
            {mode === "catalog" ? (
              <option value="catalog">{productName}, from the catalog</option>
            ) : null}
            <option value="find">Find in the catalog</option>
            <option value="new">A new item</option>
            {fitting.length ? <option value="item">Another item in inventory</option> : null}
            <option value="skip">Leave it out</option>
          </select>
        </label>
        {d.kind === "item" && mode === "item" && !finding ? (
          <label className="field">
            <span>Item</span>
            <select
              className="select"
              value={d.itemId}
              onChange={(e) => choose({ kind: "item", itemId: e.target.value })}
            >
              {fitting.map((i) => (
                <option key={i.id} value={i.id}>
                  {itemChoice(i)}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {d.kind === "new" && !finding ? (
          <>
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={d.name}
                onChange={(e) => onChange({ ...choice, decision: { ...d, name: e.target.value } })}
              />
            </label>
            <label className="field">
              <span>Category</span>
              <select
                className="select"
                value={d.category}
                onChange={(e) =>
                  onChange({
                    ...choice,
                    decision: { ...d, category: e.target.value as Category },
                    // Another kind comes in other packs: guess again.
                    pack: undefined,
                  })
                }
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {categoryLabel(c)}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : null}
        {deciding ? (
          <div className="field">
            <span>Comes in</span>
            <PackPicker
              key={`${d.kind}|${category ?? ""}|${choice.pack === undefined ? "guess" : "chosen"}`}
              label={`What ${g.name} comes in`}
              dimension={g.dimension}
              category={category}
              displayUnit={into?.displayUnit ?? g.lots[0]?.unit ?? "lb"}
              value={pack}
              onChange={(next) => onChange({ ...choice, pack: next })}
            />
          </div>
        ) : null}
      </div>
      {deciding ? <p className="import-source">{packNote}</p> : null}
      {finding ? (
        <CatalogFinder
          group={g}
          items={items}
          onPick={(product) => {
            setFinding(false);
            choose({ kind: "catalog", productId: product.id }, product);
          }}
          onCancel={() => setFinding(false)}
        />
      ) : d.kind === "undecided" ? (
        <Undecided group={g} items={items} onPick={chooseOffered} />
      ) : (
        <p className="import-source">
          {d.kind === "catalog"
            ? into
              ? `Goes into ${into.displayName}, already in inventory from the catalog's ${productName}.`
              : `Comes in as the catalog's ${productName}, with its maker, category and figures. Invoices for it later find this same item.`
            : d.kind === "item"
              ? `Goes into ${into?.displayName ?? "an item"}, already in inventory.`
              : d.kind === "new"
                ? "Not matched to the catalog: comes in as a new item. If the catalog has it under another name, find it there, so invoices for it later find the same item."
                : "Left out of the import."}
        </p>
      )}
      {already && into ? (
        <p className="import-overlap">
          <TriangleAlert size={14} />{" "}
          {formatQuantity(into.onHand.quantity, { unit: into.displayUnit })} already in inventory.
          Its lots here start unticked so nothing is counted twice; tick the ones to add.
        </p>
      ) : null}
      {d.kind === "skip" ? null : (
        <div className="table-wrap">
          <table className="table import-lots">
            <thead>
              <tr>
                <th aria-label="Bring in" />
                <th>Lot</th>
                <th className="num">On hand</th>
                <th>Received</th>
                <th className="num">Cost</th>
                {category === "hop" ? <th>Region</th> : null}
                {category === "hop" ? <th>Grower</th> : null}
                <th>Goes to</th>
              </tr>
            </thead>
            <tbody>
              {g.lots.map((l) => {
                const lot = choice.lots[String(l.index)] ?? { include: true, locationId: null };
                const setLot = (patch: Partial<typeof lot>) =>
                  onChange({
                    ...choice,
                    lots: { ...choice.lots, [String(l.index)]: { ...lot, ...patch } },
                  });
                return (
                  <tr key={l.index} className={lot.include ? undefined : "faint"}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Bring in lot ${l.lotCode}`}
                        checked={lot.include}
                        onChange={(e) => setLot({ include: e.target.checked })}
                      />
                    </td>
                    <td>{l.lotCode}</td>
                    <td className="num">
                      {quantityText(l.quantity, l.unit)}
                      {usable && packSize ? (
                        <span className="muted">
                          {" "}
                          (
                          {formatQuantity(
                            toCanonical({ value: l.quantity, unit: l.unit }, g.dimension),
                            {
                              pack: { name: usable.name, size: packSize },
                            },
                          )}
                          )
                        </span>
                      ) : null}
                    </td>
                    <td>{l.receivedOn ? showDate(l.receivedOn) : "Not known"}</td>
                    <td className="num">
                      {l.price ? `$${l.price.amount} per ${UNITS[l.price.per].label}` : ""}
                    </td>
                    {category === "hop" ? (
                      <td>
                        <RegionInput
                          label={`Where lot ${l.lotCode} was grown`}
                          value={lot.region ?? ""}
                          disabled={!lot.include}
                          onChange={(region) => setLot({ region })}
                        />
                      </td>
                    ) : null}
                    {category === "hop" ? (
                      <td>
                        <input
                          className="input"
                          aria-label={`The farm that grew lot ${l.lotCode}`}
                          value={lot.grower ?? ""}
                          disabled={!lot.include}
                          onChange={(e) => setLot({ grower: e.target.value })}
                        />
                      </td>
                    ) : null}
                    <td>
                      <select
                        className="select"
                        aria-label={`Where lot ${l.lotCode} goes`}
                        value={placeOf(l.index)}
                        disabled={!lot.include}
                        onChange={(e) => setLot({ locationId: e.target.value })}
                      >
                        {locations.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
/**
 * A name nothing here or in the catalog answers word for word: the catalog products closest to it, to
 * tap. A close one is written differently but probably the same ("Torrified" for "Torrefied", or the
 * catalog's "Premier Flaked Oats" for "Canada Malting Flaked Oats"); the rest only share some words.
 */
function Undecided({
  group: g,
  items,
  onPick,
}: {
  group: Group;
  items: Item[];
  onPick: (c: Candidate) => void;
}) {
  const offered = g.suggestion.candidates;
  return (
    <>
      <p className="import-source">
        <strong>Not decided yet.</strong>{" "}
        {offered.length
          ? "Nothing has this name exactly. If one of these is it, pick it, so invoices for it later find the same item; otherwise find it in the catalog, make it a new item, or leave it out."
          : "Nothing here or in the catalog has a name like this. Find it in the catalog under another name, make it a new item, or leave it out."}
      </p>
      {offered.length ? (
        <div className="catalog-results">
          {offered.map((c) => {
            // Where the import would put its lots, if it is picked.
            const here = itemFor(g, { kind: "catalog", productId: c.id }, items, pickedOf(c));
            return (
              <button
                key={c.id}
                type="button"
                className="catalog-result"
                aria-label={`${g.name} is ${c.name}`}
                onClick={() => onPick(c)}
              >
                <span>
                  <strong>{c.name}</strong>
                  <span className="muted">
                    {[categoryLabel(c.category), c.hop ? HOP_FORM_LABELS[c.hop.form] : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className={c.close ? "close-fit" : "faint"}>
                  {c.close ? "Probably this, check it" : "Shares some words"}
                  {here ? `, in inventory as ${here.displayName}` : ""}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </>
  );
}
/**
 * Searching the catalog for what one of Ollie's names is, as Add item searches it: by name, maker or
 * item number. A product counted another way than the report counts it cannot be chosen.
 */
function CatalogFinder({
  group: g,
  items,
  onPick,
  onCancel,
}: {
  group: Group;
  items: Item[];
  onPick: (product: { id: string; name: string; category: Category }) => void;
  onCancel: () => void;
}) {
  const [q, setQ] = useState(g.name);
  const { data: products = [], isFetching } = useCatalog(q, null);
  return (
    <div className="catalog-finder">
      <label className="field">
        <span>Find in the catalog</span>
        <input
          className="input"
          type="search"
          value={q}
          placeholder="Name, maker or item number"
          // biome-ignore lint/a11y/noAutofocus: the search appears because Find in the catalog was chosen
          autoFocus
          onChange={(e) => setQ(e.target.value)}
        />
      </label>
      {q.trim().length >= 2 && !isFetching && products.length === 0 ? (
        <p className="muted">Nothing in the catalog matches “{q.trim()}”.</p>
      ) : null}
      <div className="catalog-results">
        {products.map((p) => {
          const fits = countedWays(p.category).includes(g.dimension);
          const counted = defaultsFor(p.category).dimension;
          const name = withMaker(p.manufacturer, p.name);
          const here = items.find((i) => p.adoptedItemIds.includes(i.id) && !i.archived);
          return (
            <button
              key={p.id}
              type="button"
              className="catalog-result"
              disabled={!fits}
              onClick={() => onPick({ id: p.id, name, category: p.category as Category })}
            >
              <span>
                <strong>{p.name}</strong>
                <span className="muted">
                  {[
                    p.manufacturer,
                    categoryLabel(p.category),
                    p.hop ? HOP_FORM_LABELS[p.hop.form] : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="faint">
                {!fits
                  ? `Counted ${DIMENSION_WORDS[counted]}; Ollie counts this ${DIMENSION_WORDS[g.dimension]}`
                  : here
                    ? `In inventory as ${here.displayName}`
                    : ""}
              </span>
            </button>
          );
        })}
      </div>
      <div className="row">
        <button type="button" className="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
